import React, { useState, useEffect } from 'react';
import api from '../services/api';

const AlertsTab = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('active'); // active, resolved, all
    const [showResolveModal, setShowResolveModal] = useState(false);
    const [selectedAlert, setSelectedAlert] = useState(null);
    const [resolveNotes, setResolveNotes] = useState('');

    useEffect(() => {
        fetchAlerts();
    }, [filter]);

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const params = { limit: 50 };
            if (filter !== 'all') params.status = filter;

            const response = await api.get('/cash-box/alerts', { params });
            setAlerts(response.data.alerts);
        } catch (error) {
            console.error('Error fetching alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolveClick = (alert) => {
        setSelectedAlert(alert);
        setResolveNotes('');
        setShowResolveModal(true);
    };

    const submitResolve = async () => {
        if (!selectedAlert) return;

        try {
            await api.post(`/cash-box/alerts/${selectedAlert.id}/resolve`, {
                notes: resolveNotes
            });
            setShowResolveModal(false);
            fetchAlerts(); // Refresh list
        } catch (error) {
            console.error('Error resolving alert:', error);
            alert('Error al resolver alerta');
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'critical': return 'bg-red-100 text-red-800 border-red-200';
            case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            default: return 'bg-blue-100 text-blue-800 border-blue-200';
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('es-CO');
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Alertas de Seguridad</h2>
                <div className="flex bg-gray-100 rounded-lg p-1">
                    <button
                        onClick={() => setFilter('active')}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${filter === 'active' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Activas
                    </button>
                    <button
                        onClick={() => setFilter('resolved')}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${filter === 'resolved' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Resueltas
                    </button>
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${filter === 'all' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Todas
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-10 text-gray-500">Cargando alertas...</div>
            ) : alerts.length > 0 ? (
                <div className="space-y-4">
                    {alerts.map(alert => (
                        <div key={alert.id} className={`border rounded-lg p-4 flex flex-col md:flex-row gap-4 ${alert.status === 'resolved' ? 'bg-gray-50 opacity-75' : 'bg-white shadow-sm'}`}>
                            <div className="flex-1">
                                <div className="flex items-start gap-3">
                                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${getSeverityColor(alert.severity)}`}>
                                        {alert.severity}
                                    </span>
                                    <span className="text-xs text-gray-500 mt-1">
                                        {formatDate(alert.created_at)}
                                    </span>
                                </div>
                                <h3 className="font-semibold text-gray-800 mt-2">{alert.message}</h3>
                                <div className="text-sm text-gray-600 mt-1">
                                    Tipo: <span className="font-mono bg-gray-100 px-1 rounded">{alert.type}</span>
                                </div>
                                {alert.status === 'resolved' && (
                                    <div className="mt-2 text-sm text-green-700 bg-green-50 p-2 rounded border border-green-100">
                                        ✅ Resuelto por {alert.resolved_by_name} el {formatDate(alert.resolved_at)}
                                        {alert.metadata && JSON.parse(alert.metadata).resolution_notes && (
                                            <div className="italic mt-1">"{JSON.parse(alert.metadata).resolution_notes}"</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {alert.status === 'active' && (
                                <div className="flex items-center">
                                    <button
                                        onClick={() => handleResolveClick(alert)}
                                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 font-medium text-sm whitespace-nowrap"
                                    >
                                        Resolver
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <p className="text-gray-500">No hay alertas {filter === 'active' ? 'activas' : 'registradas'}</p>
                </div>
            )}

            {/* Resolve Modal */}
            {showResolveModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-4">Resolver Alerta</h3>
                        <p className="text-sm text-gray-600 mb-4 bg-gray-50 p-3 rounded">
                            {selectedAlert?.message}
                        </p>

                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Notas de la resolución
                        </label>
                        <textarea
                            value={resolveNotes}
                            onChange={(e) => setResolveNotes(e.target.value)}
                            className="w-full border rounded-md p-2 h-24 mb-4 focus:ring-2 focus:ring-blue-500"
                            placeholder="Describa cómo se solucionó el problema..."
                        ></textarea>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowResolveModal(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={submitResolve}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                            >
                                Marcar como Resuelto
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlertsTab;
